/**
 * Feature-registry contract (docs/06 §3).
 *
 * One schema, three consumers: the CI completeness check (gate 12), the
 * registry sync that mirrors the seed YAML into the `feature_registry` table at
 * deploy, and eventually the /admin/feature-registry viewer. Entries are never
 * deleted — removal flips status to `deprecated` with a release reference.
 */

import { z } from 'zod';

import { readableFallback } from './vocabulary.ts';

/**
 * Where a feature stands. Entries are never deleted — removal flips to
 * `deprecated` with a release reference.
 */
export const FEATURE_LIFECYCLES = ['planned', 'in_progress', 'live', 'deprecated'] as const;
export type FeatureLifecycle = (typeof FEATURE_LIFECYCLES)[number];

/** The words the viewer shows. `in_progress` is the one that must not leak. */
export const FEATURE_LIFECYCLE_LABEL: Record<FeatureLifecycle, string> = {
  planned: 'Planned',
  in_progress: 'In progress',
  live: 'Live',
  deprecated: 'Deprecated',
};

/**
 * The label, with an explicit fallback for a status this map has not been
 * taught.
 *
 * The viewer used to hold an untyped `Record<string, string>` and fall back to
 * `?? feature.status` — so the day the seed grows a fifth status, the screen
 * prints the raw value. This surface genuinely IS open-ended at runtime: the
 * table is mirrored from the seed YAML at deploy, and a newer seed can reach an
 * older bundle. The fallback therefore opens the underscores and capitalises,
 * the same treatment `entityKindLabel` gives an unknown entity kind — legible,
 * and never blank, which would be worse than either.
 */
export function featureLifecycleLabel(status: string): string {
  const known = (FEATURE_LIFECYCLE_LABEL as Record<string, string | undefined>)[status];
  if (known !== undefined) return known;
  return readableFallback(status, 'Status not recorded');
}

export const featureSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  status: z.enum(FEATURE_LIFECYCLES),
  owner: z.string().min(1),
  routes: z.array(z.string()).default([]),
  entities: z.array(z.string()).default([]),
  permission_keys: z.array(z.string()).default([]),
  audit_events: z.array(z.string()).default([]),
  docs: z.array(z.string()).default([]),
  notes: z.string().optional(),
});

export const registrySchema = z.object({ features: z.array(featureSchema).min(1) });

export type Feature = z.infer<typeof featureSchema>;
